import {
  Button,
  ButtonMode,
  ButtonSize,
  Divider,
  FilterBox,
  Loader,
  Modal,
  ModalWidth,
  useGlobalModalContext,
} from '@creditsights/styleguide-web';
import { TreeSelectHandle } from '@creditsights/styleguide-web/components/TreeSelect/TreeSelect';
import classNames from 'classnames/bind';
import React, { ReactElement, useMemo, useRef, useState } from 'react';
import useIssuersFilterContext from '../../../hooks/filters/useIssuersFilterContext';
import useFetchUserPermissions from '../../../hooks/permissions/useFetchUserPermissions';
import useFetchFilters from '../../../hooks/tags/useFetchFilters';
import {
  CSViewOption,
  DateFilterOption,
  FILTER_TYPE_LABELS_SINGULAR,
  KeywordFilterOption,
  RISK_FILTER_TYPE_LABELS,
  CORE_SCORE_FILTER_TYPE_LABELS,
  IssuersFilterOption,
  RiskFilterType,
} from '../../../models';
import { convertToTreeModel } from '../../../utils/filterUtils';
import {
  hasLevel2Permissions,
  hasLevel3Permissions,
  hasPermissionToRp,
  hasPermissionToCoreScores,
} from '../../../utils/permissionsUtils';
import { getApiFilterTypes } from '../../FiltersBar/FiltersBar.logic';
import { isFilterNotSearchable } from '../Filters.logic';
import RangeFilterBox from '../RangeFilterBox';
import {
  COMPANY_CORE_SCORE_FILTERS,
  COMPANY_CORE_SCORE_FILTERS_GROUPS,
  COMPANY_FILTER_GROUPS,
  COMPANY_RISK_FILTERS,
  CS_VIEW_DATA,
  DISABLED_FILTER_TYPES,
  LEVEL_3_FILTER_TYPES,
  RANGE_FILTER_TYPES,
  RISK_FILTERS_OPTION_TREE_MAP,
  SCORE_FILTERS,
} from '../Filters.constants';
import styles from './IssuersFilterModal.module.scss';

const cx = classNames.bind(styles);

const { quickFilters, riskFilters } = COMPANY_FILTER_GROUPS;

enum IssuersFilterModalTab {
  QUICK_FILTERS = 'QUICK_FILTERS',
  RISK_FILTERS = 'RISK_FILTERS',
  CORE_FILTERS = 'CORE_FILTERS',
}

const IssuersFilterModal = (): ReactElement => {
  const { closeModal } = useGlobalModalContext();

  const { activeFilters, resetFilters, setTags, setRecWeightings, setRange } = useIssuersFilterContext();

  const apiFilterTypes = getApiFilterTypes(quickFilters);
  const { data: fetchedFilters = [], isLoading } = useFetchFilters(apiFilterTypes);
  const { data: userPermissions } = useFetchUserPermissions();

  const filters = useMemo(
    () =>
      fetchedFilters.map((filter) => ({
        type: filter.type,
        options: convertToTreeModel(filter),
      })),
    [fetchedFilters]
  );

  const [activeTab, setActiveTab] = useState(IssuersFilterModalTab.QUICK_FILTERS);
  const refs = useRef<Record<string, TreeSelectHandle | null>>({});

  const resetModalFilters = () => {
    resetFilters();
    Object.values(refs.current).forEach((ref) => ref?.reset());
  };

  const getTreeRefSetter = (filterType: string) => (ref: TreeSelectHandle | null) => {
    refs.current[filterType] = ref;
  };

  const renderTabs = () => (
    <span className={cx('tabs')}>
      <Button
        mode={activeTab === IssuersFilterModalTab.QUICK_FILTERS ? ButtonMode.PRIMARY : ButtonMode.SECONDARY}
        onClick={() => setActiveTab(IssuersFilterModalTab.QUICK_FILTERS)}
        size={ButtonSize.SMALL}
      >
        Quick Filters
      </Button>

      {hasPermissionToCoreScores(userPermissions) && (
        <Button
          mode={activeTab === IssuersFilterModalTab.CORE_FILTERS ? ButtonMode.PRIMARY : ButtonMode.SECONDARY}
          onClick={() => setActiveTab(IssuersFilterModalTab.CORE_FILTERS)}
          size={ButtonSize.SMALL}
        >
          CoreScore
        </Button>
      )}

      {hasPermissionToRp(userPermissions) && (
        <Button
          mode={activeTab === IssuersFilterModalTab.RISK_FILTERS ? ButtonMode.PRIMARY : ButtonMode.SECONDARY}
          onClick={() => setActiveTab(IssuersFilterModalTab.RISK_FILTERS)}
          size={ButtonSize.SMALL}
        >
          Risk Filters
        </Button>
      )}
    </span>
  );

  const renderFilterOption = (filterType: IssuersFilterOption, riskFiltersToRender?) => {
    if (filterType === DateFilterOption || filterType === KeywordFilterOption) {
      return null;
    }

    const isFilterDisabled =
      filterType === RiskFilterType.fas
        ? !hasLevel2Permissions(userPermissions) && !hasLevel3Permissions(userPermissions)
        : DISABLED_FILTER_TYPES.includes(filterType) ||
          (!hasLevel3Permissions(userPermissions) && LEVEL_3_FILTER_TYPES.includes(filterType));

    if (filterType === CSViewOption) {
      return (
        <FilterBox
          data={CS_VIEW_DATA}
          placeholder="CS View"
          dataTestId="filter-box"
          onChange={setRecWeightings}
          checkedKeys={activeFilters.recWeightings || []}
          key={filterType}
          showSearch={false}
          ref={getTreeRefSetter(filterType)}
          disabled={isFilterDisabled}
        />
      );
    }

    if (RANGE_FILTER_TYPES.includes(filterType)) {
      if (riskFiltersToRender && filterType === RiskFilterType.cre_1y) {
        return null;
      }

      if (filterType !== RiskFilterType.cqs || !riskFiltersToRender) {
        return (
          <RangeFilterBox
            onChange={(range) => setRange(filterType, range)}
            title={RISK_FILTER_TYPE_LABELS[filterType]}
            initialRange={activeFilters.ranges?.[filterType]}
            key={filterType}
            isDisabled={isFilterDisabled}
          />
        );
      }
      const isCre1yFilterDisabled =
        DISABLED_FILTER_TYPES.includes(RiskFilterType.cre_1y) ||
        (hasLevel2Permissions(userPermissions) && !hasLevel3Permissions(userPermissions));

      return (
        <div className={cx('filter-group')}>
          <RangeFilterBox
            onChange={(range) => setRange(filterType, range)}
            title={RISK_FILTER_TYPE_LABELS[filterType]}
            initialRange={activeFilters.ranges?.[filterType]}
            key={filterType}
            isDisabled={isFilterDisabled}
          />
          <RangeFilterBox
            onChange={(range) => setRange(RiskFilterType.cre_1y, { ...range, isFractional: true })}
            title={RISK_FILTER_TYPE_LABELS[RiskFilterType.cre_1y]}
            initialRange={activeFilters.ranges?.[RiskFilterType.cre_1y]}
            key={RiskFilterType.cre_1y}
            isDisabled={isCre1yFilterDisabled}
            asPercent
          />
        </div>
      );
    }

    if (COMPANY_RISK_FILTERS.includes(filterType)) {
      const options = RISK_FILTERS_OPTION_TREE_MAP.get(filterType);

      return (
        <FilterBox
          data={options || []}
          placeholder={RISK_FILTER_TYPE_LABELS[filterType]}
          dataTestId="filter-box"
          onChange={(tags) => setTags(filterType, tags)}
          checkedKeys={activeFilters.tags?.[filterType] || []}
          key={filterType}
          showSearch={!isFilterNotSearchable(filterType)}
          ref={getTreeRefSetter(filterType)}
          disabled={isFilterDisabled}
        />
      );
    }

    if (COMPANY_CORE_SCORE_FILTERS.includes(filterType)) {
      const scoreFilter = SCORE_FILTERS.find((scoreFilter) => scoreFilter.type === filterType);
      if (!scoreFilter) {
        return null;
      }
      return (
        <FilterBox
          data={scoreFilter?.options || []}
          placeholder={CORE_SCORE_FILTER_TYPE_LABELS[scoreFilter?.type]}
          onChange={(tags) => setTags(filterType, tags)}
          checkedKeys={activeFilters.tags?.[filterType] || []}
          key={filterType}
          showSearch={false}
          ref={getTreeRefSetter(filterType)}
          disabled={isFilterDisabled}
        />
      );
    }

    const filter = filters.find((feedFilter) => feedFilter.type === filterType);

    if (!filter) {
      return null;
    }

    return (
      <FilterBox
        data={filter.options}
        placeholder={FILTER_TYPE_LABELS_SINGULAR[filter.type]}
        dataTestId="filter-box"
        onChange={(tags) => setTags(filterType, tags)}
        checkedKeys={activeFilters.tags?.[filterType] || []}
        key={filterType}
        showSearch={!isFilterNotSearchable(filterType)}
        ref={getTreeRefSetter(filterType)}
        disabled={isFilterDisabled}
      />
    );
  };

  const headerAction = (
    <Button
      mode={ButtonMode.PLAIN}
      className={cx('header-button')}
      onClick={resetModalFilters}
      dataTestId="reset-filters-button"
    >
      Reset Filters
    </Button>
  );

  const renderContent = () => {
    if (isLoading) {
      return <Loader />;
    }
    if (activeTab === IssuersFilterModalTab.QUICK_FILTERS) {
      return <div className={cx('filters')}>{quickFilters.map(renderFilterOption)}</div>;
    }
    if (activeTab === IssuersFilterModalTab.RISK_FILTERS) {
      const riskFiltersToRender = [...riskFilters];
      let cqsCreGroupCheck = false;
      if (riskFiltersToRender.includes(RiskFilterType.cre_1y) && riskFiltersToRender.includes(RiskFilterType.cqs)) {
        cqsCreGroupCheck = true;
        riskFiltersToRender.filter((filterType) => filterType !== RiskFilterType.cre_1y);
      }
      return (
        <div className={cx('filters')}>
          {riskFiltersToRender.map((filterType) => renderFilterOption(filterType, cqsCreGroupCheck))}
        </div>
      );
    }
    if (activeTab === IssuersFilterModalTab.CORE_FILTERS) {
      return (
        <>
          {COMPANY_CORE_SCORE_FILTERS_GROUPS.map((item) => {
            return <div className={cx('score-filters')}>{item.group.map(renderFilterOption)}</div>;
          })}
        </>
      );
    }

    return null;
  };

  return (
    <Modal
      headerTitle="Filter Results"
      open
      onClose={closeModal}
      width={ModalWidth.ExtraLarge}
      headerAction={headerAction}
      dataTestId="filters-modal"
      className={cx('body')}
    >
      <div className={cx('content')}>
        <div className={cx('header')}>{renderTabs()}</div>
        <Divider className={cx('divider')} />
        {renderContent()}
      </div>
    </Modal>
  );
};

export default IssuersFilterModal;
